// src/components/SideNav.js
import React, { useState } from 'react';
import { Drawer, List, Collapse, ListItemButton, ListItemText, ListItem } from '@mui/material';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ListItemLink from './ListItemLink';
import { listItemButtonStyle } from '../styles';

const SideNav = ({ drawerWidth, isMobile, mobileOpen, onClose }) => {
  const [open, setOpen] = useState({ accounts: false, timeseries: false, plots: false, strategies: false, equity: false });

  const handleClick = (section) => {
    setOpen((prevState) => ({ ...prevState, [section]: !prevState[section] }));
  };

  // Close drawer on mobile when a menu item is clicked
  const handleNavigate = isMobile ? onClose : undefined;

  const drawerContent = (
    <>
    <Box display="flex" alignItems="center" p={2}>
        <Avatar src="/282.png" alt="Logo" sx={{ width: 40, height: 40, mr: 1 }} />
        <Typography variant="h6" noWrap>
          Avril
        </Typography>
      </Box>


      <List>
        <ListItemLink to="/openPositions" primary="Open Positions" onNavigate={handleNavigate} />
        <ListItemLink to="/scenarios" primary="Scenarios" onNavigate={handleNavigate} />
        <ListItemLink to="/portfolios" primary="Portfolios" onNavigate={handleNavigate} />

        {/* Accounts */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleClick('accounts')} sx={listItemButtonStyle(open.accounts)}>
            <ListItemText primary="Accounts" />
            {open.accounts ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={open.accounts} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemLink to="/accounts/balance" primary="Balance" isSubItem onNavigate={handleNavigate} />
            <ListItemLink to="/accounts/list" primary="List" isSubItem onNavigate={handleNavigate} />
          </List>
        </Collapse>

        {/* Time Series */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleClick('timeseries')} sx={listItemButtonStyle(open.timeseries)}>
            <ListItemText primary="Time Series" />
            {open.timeseries ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={open.timeseries} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemLink to="/timeseries/explorer" primary="Explorer" isSubItem onNavigate={handleNavigate} />
          </List>
        </Collapse>

        {/* FRB/US Model */}
        <ListItemLink to="/pyfrbus" primary="FRB/US Model" onNavigate={handleNavigate} />

        <ListItemLink to="/macro" primary="Macro" onNavigate={handleNavigate} />

        <ListItemLink to="/option" primary="Option" onNavigate={handleNavigate} />

        {/* <Divider /> */}

        {/* Plots */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleClick('plots')} sx={listItemButtonStyle(open.plots)}>
            <ListItemText primary="Plots" />
            {open.plots ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={open.plots} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemLink to="/plot/index" primary="Index" isSubItem onNavigate={handleNavigate} />
            <ListItemLink to="/plot/commodities" primary="Commodities" isSubItem onNavigate={handleNavigate} />
            <ListItemLink to="/plot/fx" primary="FX" isSubItem onNavigate={handleNavigate} />
            <ListItemLink to="/plot/trend" primary="Trend" isSubItem onNavigate={handleNavigate} />
            <ListItemLink to="/plot/futures/CL" primary="Futures" isSubItem onNavigate={handleNavigate} />
            <ListItemLink to="/plot/sandbox" primary="Sandbox" isSubItem onNavigate={handleNavigate} />
          </List>
        </Collapse>

        {/* <Divider /> */}

        {/* Strategies */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleClick('strategies')} sx={listItemButtonStyle(open.strategies)}>
            <ListItemText primary="Strategies" />
            {open.strategies ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={open.strategies} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemLink to="/strategies/avtrcn" primary="AVTRCN" isSubItem onNavigate={handleNavigate} />
            <ListItemLink to="/strategies/macro-demand" primary="Macro Demand" isSubItem onNavigate={handleNavigate} />
            <ListItemLink to="/strategies/fiscal-pressure" primary="Fiscal Pressure" isSubItem onNavigate={handleNavigate} />
            <ListItemLink to="/strategies/gc-carry" primary="GC Carry" isSubItem onNavigate={handleNavigate} />
          </List>
        </Collapse>

        {/* Equity */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleClick('equity')} sx={listItemButtonStyle(open.equity)}>
            <ListItemText primary="Equity" />
            {open.equity ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={open.equity} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemLink to="/equity/mqg" primary="MQG" isSubItem onNavigate={handleNavigate} />
          </List>
        </Collapse>

      </List>
    </>
  );

  // Mobile: temporary drawer that slides in from left
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: permanent drawer always visible
  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', md: 'block' },
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export default SideNav;
